import { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Avatar, Spinner } from '@components/ui';
import { MemberModal, EditMemberModal } from '@components/features';
import { useProjectMembers, useAddProjectMember, useRemoveProjectMember } from '@hooks';
import { useThemeStore } from '@store';

const roleLabels = {
  OWNER: 'Propietario',
  ADMIN: 'Administrador',
  MEMBER: 'Miembro',
  VIEWER: 'Observador',
  admin: 'Administrador',
  project_manager: 'Project Manager',
  developer: 'Desarrollador',
  designer: 'Diseñador',
  qa: 'QA',
  viewer: 'Visualizador',
};

// Apple HIG: Colores semánticos con buen contraste (4.5:1 mínimo)
const roleColors = {
  OWNER: { bg: '#FEF3C7', text: '#92400E', icon: '👑' },
  ADMIN: { bg: '#EDE9FE', text: '#6D28D9', icon: '⚙️' },
  MEMBER: { bg: '#D1FAE5', text: '#065F46', icon: '👤' },
  VIEWER: { bg: '#F1F5F9', text: '#475569', icon: '👁️' },
  admin: { bg: '#EDE9FE', text: '#6D28D9', icon: '⚙️' },
  project_manager: { bg: '#DBEAFE', text: '#1E40AF', icon: '📋' },
  developer: { bg: '#D1FAE5', text: '#065F46', icon: '💻' },
  designer: { bg: '#FCE7F3', text: '#9D174D', icon: '🎨' },
  qa: { bg: '#FEF3C7', text: '#92400E', icon: '🔍' },
  viewer: { bg: '#F1F5F9', text: '#475569', icon: '👁️' },
};

// Apple HIG: Iconos consistentes para acciones
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const RemoveIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TaskIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const PointsIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

// Apple HIG: Iconos para toggle de vista
const GridIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

const ListIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

function MemberCard({ member, onRemove, onEdit, isRemoving, isDark }) {
  const user = member.user || {};
  const initials = user.name
    ? user.name.split(' ').map((n) => n[0]).join('').toUpperCase()
    : '??';

  const taskCount = member.assignedTasks?.length || member.taskCount || 0;
  const inProgressCount = member.assignedTasks?.filter(t => t.task?.status === 'progress').length || member.inProgressCount || 0;
  const totalHours = member.totalHours || 0;
  const totalPoints = member.totalPoints || 0;

  const roleStyle = roleColors[member.role] || roleColors.MEMBER;

  const handleRemove = () => {
    if (window.confirm(`¿Estás seguro de eliminar a ${user.name} del equipo?`)) {
      onRemove(member.userId || user.id);
    }
  };

  const handleEdit = () => {
    onEdit(member);
  };

  // Apple HIG: Estilos con jerarquía visual clara
  const cardStyle = {
    position: 'relative',
    overflow: 'hidden',
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '20px',
  };

  const nameStyle = {
    fontSize: '17px', // Apple HIG: Tamaño estándar para texto principal
    fontWeight: '600',
    color: isDark ? '#F1F5F9' : '#1E293B',
    marginBottom: '2px',
    letterSpacing: '-0.01em',
  };

  const emailStyle = {
    fontSize: '13px', // Apple HIG: Texto secundario
    color: isDark ? '#94A3B8' : '#64748B',
    marginBottom: '12px',
  };

  const roleBadgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '20px', // Apple HIG: Bordes redondeados tipo pill
    fontSize: '12px',
    fontWeight: '600',
    backgroundColor: roleStyle.bg,
    color: roleStyle.text,
  };

  const statsContainerStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    padding: '16px',
    backgroundColor: isDark ? 'rgba(30, 41, 59, 0.5)' : 'rgba(248, 250, 252, 0.8)',
    borderRadius: '12px',
    marginTop: '16px',
  };

  const statItemStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  };

  const statValueStyle = {
    fontSize: '24px', // Apple HIG: Números grandes para datos importantes
    fontWeight: '700',
    color: isDark ? '#F1F5F9' : '#1E293B',
    lineHeight: '1',
  };

  const statLabelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    fontWeight: '500',
    color: isDark ? '#94A3B8' : '#64748B',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  // Apple HIG: Botones de acción con área táctil mínima
  const actionButtonStyle = {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    opacity: 0,
    transition: 'all 0.2s ease',
  };

  const actionsContainerStyle = {
    position: 'absolute',
    top: '12px',
    right: '12px',
    display: 'flex',
    gap: '4px',
  };

  return (
    <Card className="hover:shadow-lg transition-all group" style={cardStyle}>
      {/* Apple HIG: Botones de acción contextual */}
      <div style={actionsContainerStyle}>
        <button
          onClick={handleEdit}
          className="group-hover:opacity-100 hover:bg-blue-50 dark:hover:bg-blue-900/20"
          style={{ ...actionButtonStyle, color: isDark ? '#60A5FA' : '#3B82F6' }}
          title="Editar miembro"
        >
          <EditIcon />
        </button>
        <button
          onClick={handleRemove}
          disabled={isRemoving}
          className="group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-900/20"
          style={{ ...actionButtonStyle, color: isDark ? '#F87171' : '#EF4444' }}
          title="Eliminar del equipo"
        >
          <RemoveIcon />
        </button>
      </div>

      <div style={headerStyle}>
        <Avatar initials={initials} size="lg" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={nameStyle} className="truncate">{user.name}</h3>
          <p style={emailStyle} className="truncate">{user.email}</p>
          <span style={roleBadgeStyle}>
            <span>{roleStyle.icon}</span>
            {roleLabels[member.role] || member.role}
          </span>
        </div>
      </div>

      {/* Apple HIG: Estadísticas con jerarquía visual */}
      <div style={statsContainerStyle}>
        <div style={statItemStyle}>
          <span style={{ ...statValueStyle, color: isDark ? '#818CF8' : '#6366F1' }}>
            {taskCount}
          </span>
          <span style={statLabelStyle}>
            <TaskIcon /> Tareas
          </span>
        </div>
        <div style={statItemStyle}>
          <span style={{ ...statValueStyle, color: isDark ? '#34D399' : '#10B981' }}>
            {inProgressCount}
          </span>
          <span style={statLabelStyle}>
            <ClockIcon /> Activas
          </span>
        </div>
      </div>

      {/* Apple HIG: Información adicional con menor jerarquía */}
      {(totalHours > 0 || totalPoints > 0) && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '24px',
          marginTop: '12px',
          paddingTop: '12px',
          borderTop: `1px solid ${isDark ? '#334155' : '#E2E8F0'}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <PointsIcon />
            <span style={{ fontSize: '14px', fontWeight: '600', color: isDark ? '#F1F5F9' : '#475569' }}>
              {totalPoints}
            </span>
            <span style={{ fontSize: '12px', color: isDark ? '#64748B' : '#94A3B8' }}>pts</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <ClockIcon />
            <span style={{ fontSize: '14px', fontWeight: '600', color: isDark ? '#F1F5F9' : '#475569' }}>
              {totalHours}
            </span>
            <span style={{ fontSize: '12px', color: isDark ? '#64748B' : '#94A3B8' }}>hrs</span>
          </div>
        </div>
      )}
    </Card>
  );
}

MemberCard.propTypes = {
  member: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  isRemoving: PropTypes.bool,
  isDark: PropTypes.bool,
};

// Apple HIG: Componente de lista compacta
function MemberListItem({ member, onRemove, onEdit, isRemoving, isDark }) {
  const user = member.user || {};
  const initials = user.name
    ? user.name.split(' ').map((n) => n[0]).join('').toUpperCase()
    : '??';

  const taskCount = member.assignedTasks?.length || member.taskCount || 0;
  const inProgressCount = member.assignedTasks?.filter(t => t.task?.status === 'progress').length || member.inProgressCount || 0;
  const totalHours = member.totalHours || 0;
  const totalPoints = member.totalPoints || 0;

  const roleStyle = roleColors[member.role] || roleColors.MEMBER;

  const handleRemove = () => {
    if (window.confirm(`¿Estás seguro de eliminar a ${user.name} del equipo?`)) {
      onRemove(member.userId || user.id);
    }
  };

  const handleEdit = () => {
    onEdit(member);
  };

  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px 20px',
    backgroundColor: isDark ? '#1E293B' : '#FFFFFF',
    borderBottom: `1px solid ${isDark ? '#334155' : '#F1F5F9'}`,
    transition: 'background-color 0.15s ease',
  };

  const roleBadgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    borderRadius: '16px',
    fontSize: '11px',
    fontWeight: '600',
    backgroundColor: roleStyle.bg,
    color: roleStyle.text,
  };

  const statStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '13px',
    color: isDark ? '#94A3B8' : '#64748B',
  };

  const statValueStyle = {
    fontWeight: '600',
    color: isDark ? '#F1F5F9' : '#1E293B',
  };

  return (
    <div
      style={rowStyle}
      className="group hover:bg-slate-50 dark:hover:bg-slate-800/50"
    >
      {/* Avatar */}
      <Avatar initials={initials} size="md" />

      {/* Info principal */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '15px',
          fontWeight: '600',
          color: isDark ? '#F1F5F9' : '#1E293B',
          marginBottom: '2px'
        }} className="truncate">
          {user.name}
        </div>
        <div style={{
          fontSize: '13px',
          color: isDark ? '#64748B' : '#94A3B8'
        }} className="truncate">
          {user.email}
        </div>
      </div>

      {/* Rol */}
      <div style={{ width: '140px' }}>
        <span style={roleBadgeStyle}>
          <span>{roleStyle.icon}</span>
          {roleLabels[member.role] || member.role}
        </span>
      </div>

      {/* Estadísticas */}
      <div style={{ display: 'flex', gap: '24px', width: '200px' }}>
        <div style={statStyle}>
          <TaskIcon />
          <span style={statValueStyle}>{taskCount}</span>
          <span>tareas</span>
        </div>
        <div style={statStyle}>
          <ClockIcon />
          <span style={statValueStyle}>{inProgressCount}</span>
          <span>activas</span>
        </div>
      </div>

      {/* Puntos y horas */}
      <div style={{ display: 'flex', gap: '16px', width: '120px' }}>
        {totalPoints > 0 && (
          <div style={statStyle}>
            <span style={statValueStyle}>{totalPoints}</span>
            <span>pts</span>
          </div>
        )}
        {totalHours > 0 && (
          <div style={statStyle}>
            <span style={statValueStyle}>{totalHours}</span>
            <span>h</span>
          </div>
        )}
      </div>

      {/* Botones de acción */}
      <div style={{ display: 'flex', gap: '4px' }}>
        <button
          onClick={handleEdit}
          className="opacity-0 group-hover:opacity-100 hover:bg-blue-50 dark:hover:bg-blue-900/20"
          style={{
            padding: '8px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            color: isDark ? '#60A5FA' : '#3B82F6',
            transition: 'all 0.2s ease',
          }}
          title="Editar miembro"
        >
          <EditIcon />
        </button>
        <button
          onClick={handleRemove}
          disabled={isRemoving}
          className="opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-900/20"
          style={{
            padding: '8px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            color: isDark ? '#F87171' : '#EF4444',
            transition: 'all 0.2s ease',
          }}
          title="Eliminar del equipo"
        >
          <RemoveIcon />
        </button>
      </div>
    </div>
  );
}

MemberListItem.propTypes = {
  member: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  isRemoving: PropTypes.bool,
  isDark: PropTypes.bool,
};

// Iconos para los paneles de resumen
const UsersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const ChartIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const CapacityIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
  </svg>
);

export default function TeamView({ projectId }) {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' o 'list'
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  const { data: membersData, isLoading, refetch: refetchMembers } = useProjectMembers(projectId);
  const addMember = useAddProjectMember();
  const removeMember = useRemoveProjectMember();

  const members = membersData?.data || [];

  // Filtrar por rol y por término de búsqueda
  const filteredMembers = members.filter(m => {
    const matchesRole = filter === 'all' || m.role === filter;
    const user = m.user || {};
    const matchesSearch = !searchTerm ||
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesRole && matchesSearch;
  });

  const roles = [...new Set(members.map(m => m.role))];

  const handleAddMember = async (data) => {
    try {
      await addMember.mutateAsync(data);
      setIsModalOpen(false);
      refetchMembers();
    } catch (error) {
      console.error('Error al agregar miembro:', error);
    }
  };

  const handleRemoveMember = async (userId) => {
    await removeMember.mutateAsync({ projectId, userId });
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setIsEditModalOpen(true);
  };

  const handleEditComplete = () => {
    setIsEditModalOpen(false);
    setEditingMember(null);
    refetchMembers();
  };

  // Calcular estadísticas
  const totalTasks = members.reduce((sum, m) => sum + (m.assignedTasks?.length || m.taskCount || 0), 0);
  const totalPoints = members.reduce((sum, m) => sum + (m.totalPoints || 0), 0);
  const totalHours = members.reduce((sum, m) => sum + (m.totalHours || 0), 0);

  // Apple HIG: Estilos base
  const pageStyle = {
    padding: '0',
  };

  // Apple HIG: Título grande (34pt) como en iOS
  const headerStyle = {
    marginBottom: '32px',
  };

  const titleStyle = {
    fontSize: '34px', // Apple HIG: Large Title
    fontWeight: '700',
    color: isDark ? '#F1F5F9' : '#1E293B',
    letterSpacing: '-0.02em',
    marginBottom: '8px',
  };

  const subtitleStyle = {
    fontSize: '15px',
    color: isDark ? '#94A3B8' : '#64748B',
  };

  // Apple HIG: Filtros tipo segmented control
  const filterContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px',
  };

  const segmentedControlStyle = {
    display: 'inline-flex',
    backgroundColor: isDark ? '#1E293B' : '#F1F5F9',
    borderRadius: '10px',
    padding: '4px',
    gap: '2px',
  };

  const segmentStyle = (isActive) => ({
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: '500',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: isActive ? (isDark ? '#334155' : '#FFFFFF') : 'transparent',
    color: isActive ? (isDark ? '#F1F5F9' : '#1E293B') : (isDark ? '#94A3B8' : '#64748B'),
    boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
  });

  // Apple HIG: Botón principal con tamaño mínimo táctil
  const addButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    fontSize: '15px',
    fontWeight: '600',
    borderRadius: '12px',
    border: 'none',
    cursor: 'pointer',
    backgroundColor: '#6366F1',
    color: '#FFFFFF',
    transition: 'all 0.2s ease',
    minHeight: '44px', // Apple HIG: Touch target mínimo
  };

  // Apple HIG: Toggle de vista estilo segmented control
  const viewToggleStyle = {
    display: 'inline-flex',
    backgroundColor: isDark ? '#1E293B' : '#F1F5F9',
    borderRadius: '8px',
    padding: '3px',
  };

  const viewToggleButtonStyle = (isActive) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '32px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    backgroundColor: isActive ? (isDark ? '#334155' : '#FFFFFF') : 'transparent',
    color: isActive ? (isDark ? '#F1F5F9' : '#1E293B') : (isDark ? '#64748B' : '#94A3B8'),
    boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
    transition: 'all 0.15s ease',
  });

  // Estilos para búsqueda
  const searchContainerStyle = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  };

  const searchIconStyle = {
    position: 'absolute',
    left: '12px',
    color: isDark ? '#64748B' : '#94A3B8',
    pointerEvents: 'none',
  };

  const searchInputStyle = {
    width: '220px',
    padding: '10px 12px 10px 38px',
    backgroundColor: isDark ? '#1E293B' : '#F8FAFC',
    border: `1px solid ${isDark ? '#334155' : '#E2E8F0'}`,
    borderRadius: '10px',
    fontSize: '14px',
    color: isDark ? '#F1F5F9' : '#1E293B',
    outline: 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  };

  // Apple HIG: Panel de resumen con iconos
  const summaryCardStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  };

  const summaryHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  };

  const summaryIconStyle = (color) => ({
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: color + '20',
    color: color,
  });

  const summaryTitleStyle = {
    fontSize: '17px',
    fontWeight: '600',
    color: isDark ? '#F1F5F9' : '#1E293B',
  };

  // Apple HIG: Estado vacío
  const emptyStateStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '64px 24px',
    textAlign: 'center',
  };

  const emptyIconStyle = {
    width: '64px',
    height: '64px',
    borderRadius: '16px',
    backgroundColor: isDark ? '#334155' : '#F1F5F9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '20px',
    color: isDark ? '#64748B' : '#94A3B8',
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {/* Apple HIG: Header con título grande */}
      <header style={headerStyle}>
        <h1 style={titleStyle}>Equipo</h1>
        <p style={subtitleStyle}>
          {members.length} {members.length === 1 ? 'miembro' : 'miembros'} en el proyecto
        </p>
      </header>

      {/* Apple HIG: Barra de filtros y acciones */}
      <div style={filterContainerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          {/* Búsqueda */}
          <div style={searchContainerStyle}>
            <span style={searchIconStyle}>
              <SearchIcon />
            </span>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar miembro..."
              style={searchInputStyle}
              onFocus={(e) => {
                e.target.style.borderColor = '#6366F1';
                e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = isDark ? '#334155' : '#E2E8F0';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <div style={segmentedControlStyle}>
            <button
              onClick={() => setFilter('all')}
              style={segmentStyle(filter === 'all')}
            >
              Todos ({members.length})
            </button>
            {roles.map((role) => {
              const roleStyle = roleColors[role] || roleColors.MEMBER;
              return (
                <button
                  key={role}
                  onClick={() => setFilter(role)}
                  style={segmentStyle(filter === role)}
                >
                  <span style={{ marginRight: '4px' }}>{roleStyle.icon}</span>
                  {roleLabels[role] || role} ({members.filter(m => m.role === role).length})
                </button>
              );
            })}
          </div>

          {/* Apple HIG: Toggle de vista Grid/List */}
          <div style={viewToggleStyle}>
            <button
              onClick={() => setViewMode('grid')}
              style={viewToggleButtonStyle(viewMode === 'grid')}
              title="Vista de tarjetas"
            >
              <GridIcon />
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={viewToggleButtonStyle(viewMode === 'list')}
              title="Vista de lista"
            >
              <ListIcon />
            </button>
          </div>
        </div>

        <button
          onClick={() => setIsModalOpen(true)}
          style={addButtonStyle}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#4F46E5'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#6366F1'}
        >
          <PlusIcon />
          Agregar Miembro
        </button>
      </div>

      {/* Miembros: Grid o Lista */}
      {filteredMembers.length > 0 ? (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredMembers.map((member) => (
              <MemberCard
                key={member.id}
                member={member}
                onRemove={handleRemoveMember}
                onEdit={handleEditMember}
                isRemoving={removeMember.isPending}
                isDark={isDark}
              />
            ))}
          </div>
        ) : (
          <Card padding="none" style={{ overflow: 'hidden' }}>
            {filteredMembers.map((member) => (
              <MemberListItem
                key={member.id}
                member={member}
                onRemove={handleRemoveMember}
                onEdit={handleEditMember}
                isRemoving={removeMember.isPending}
                isDark={isDark}
              />
            ))}
          </Card>
        )
      ) : (
        <Card>
          <div style={emptyStateStyle}>
            <div style={emptyIconStyle}>
              <UsersIcon />
            </div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: isDark ? '#F1F5F9' : '#1E293B',
              marginBottom: '8px'
            }}>
              Sin miembros
            </h3>
            <p style={{
              fontSize: '15px',
              color: isDark ? '#94A3B8' : '#64748B',
              marginBottom: '24px',
              maxWidth: '300px'
            }}>
              Agrega miembros al equipo para comenzar a colaborar en el proyecto.
            </p>
            <button
              onClick={() => setIsModalOpen(true)}
              style={addButtonStyle}
            >
              <PlusIcon />
              Agregar primer miembro
            </button>
          </div>
        </Card>
      )}

      {/* Apple HIG: Paneles de resumen con mejor jerarquía */}
      {members.length > 0 && (
        <>
          {/* Separador visual */}
          <div style={{
            margin: '48px 0 32px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          }}>
            <div style={{
              flex: 1,
              height: '1px',
              backgroundColor: isDark ? '#334155' : '#E2E8F0',
            }} />
            <span style={{
              fontSize: '13px',
              fontWeight: '600',
              color: isDark ? '#64748B' : '#94A3B8',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}>
              Resumen del equipo
            </span>
            <div style={{
              flex: 1,
              height: '1px',
              backgroundColor: isDark ? '#334155' : '#E2E8F0',
            }} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Panel: Composición del Equipo */}
          <Card>
            <div style={summaryCardStyle}>
              <div style={summaryHeaderStyle}>
                <div style={summaryIconStyle('#6366F1')}>
                  <UsersIcon />
                </div>
                <h3 style={summaryTitleStyle}>Composición</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {roles.map((role) => {
                  const roleStyle = roleColors[role] || roleColors.MEMBER;
                  const count = members.filter(m => m.role === role).length;
                  const percentage = (count / members.length) * 100;
                  return (
                    <div key={role}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '6px'
                      }}>
                        <span style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '14px',
                          color: isDark ? '#E2E8F0' : '#475569'
                        }}>
                          <span>{roleStyle.icon}</span>
                          {roleLabels[role] || role}
                        </span>
                        <span style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: isDark ? '#F1F5F9' : '#1E293B'
                        }}>
                          {count}
                        </span>
                      </div>
                      <div style={{
                        width: '100%',
                        height: '6px',
                        backgroundColor: isDark ? '#334155' : '#E2E8F0',
                        borderRadius: '3px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${percentage}%`,
                          height: '100%',
                          backgroundColor: roleStyle.text,
                          borderRadius: '3px',
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>

          {/* Panel: Carga de Trabajo */}
          <Card>
            <div style={summaryCardStyle}>
              <div style={summaryHeaderStyle}>
                <div style={summaryIconStyle('#10B981')}>
                  <ChartIcon />
                </div>
                <h3 style={summaryTitleStyle}>Carga de Trabajo</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {members.slice(0, 5).map((member) => {
                  const taskCount = member.assignedTasks?.length || member.taskCount || 0;
                  const maxTasks = Math.max(...members.map(m => m.assignedTasks?.length || m.taskCount || 0)) || 1;
                  const percentage = (taskCount / maxTasks) * 100;
                  const initials = member.user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || '??';

                  return (
                    <div key={member.id} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Avatar initials={initials} size="xs" />
                      <div style={{ flex: 1 }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: '4px'
                        }}>
                          <span style={{
                            fontSize: '13px',
                            color: isDark ? '#E2E8F0' : '#475569',
                            fontWeight: '500'
                          }} className="truncate">
                            {member.user?.name}
                          </span>
                          <span style={{
                            fontSize: '12px',
                            color: isDark ? '#94A3B8' : '#64748B'
                          }}>
                            {taskCount}
                          </span>
                        </div>
                        <div style={{
                          width: '100%',
                          height: '4px',
                          backgroundColor: isDark ? '#334155' : '#E2E8F0',
                          borderRadius: '2px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${percentage}%`,
                            height: '100%',
                            backgroundColor: percentage > 80 ? '#EF4444' : percentage > 50 ? '#F59E0B' : '#10B981',
                            borderRadius: '2px',
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
                {members.length === 0 && (
                  <p style={{ fontSize: '13px', color: isDark ? '#64748B' : '#94A3B8', textAlign: 'center' }}>
                    Sin datos de carga
                  </p>
                )}
              </div>
            </div>
          </Card>

          {/* Panel: Capacidad Total */}
          <Card>
            <div style={summaryCardStyle}>
              <div style={summaryHeaderStyle}>
                <div style={summaryIconStyle('#F59E0B')}>
                  <CapacityIcon />
                </div>
                <h3 style={summaryTitleStyle}>Capacidad Total</h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: '28px',
                    fontWeight: '700',
                    color: isDark ? '#818CF8' : '#6366F1',
                    lineHeight: '1'
                  }}>
                    {totalTasks}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: isDark ? '#94A3B8' : '#64748B',
                    marginTop: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Tareas
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: '28px',
                    fontWeight: '700',
                    color: isDark ? '#34D399' : '#10B981',
                    lineHeight: '1'
                  }}>
                    {totalPoints}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: isDark ? '#94A3B8' : '#64748B',
                    marginTop: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Puntos
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: '28px',
                    fontWeight: '700',
                    color: isDark ? '#FBBF24' : '#F59E0B',
                    lineHeight: '1'
                  }}>
                    {totalHours}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: isDark ? '#94A3B8' : '#64748B',
                    marginTop: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Horas
                  </div>
                </div>
              </div>
              {/* Indicador de promedio */}
              {members.length > 0 && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: isDark ? 'rgba(30, 41, 59, 0.5)' : 'rgba(248, 250, 252, 0.8)',
                  borderRadius: '8px',
                }}>
                  <div style={{
                    fontSize: '12px',
                    color: isDark ? '#94A3B8' : '#64748B',
                    marginBottom: '4px'
                  }}>
                    Promedio por miembro
                  </div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: isDark ? '#F1F5F9' : '#1E293B'
                  }}>
                    {(totalTasks / members.length).toFixed(1)} tareas · {(totalPoints / members.length).toFixed(0)} pts · {(totalHours / members.length).toFixed(1)}h
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
        </>
      )}

      <MemberModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleAddMember}
        projectId={projectId}
        existingMembers={members}
        isLoading={addMember.isPending}
      />

      <EditMemberModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingMember(null);
        }}
        member={editingMember}
        projectId={projectId}
        onSuccess={handleEditComplete}
      />
    </div>
  );
}

TeamView.propTypes = {
  projectId: PropTypes.number,
};
